import { useState } from 'react';
import { FileSpreadsheet, FileText, Loader2, Users, GraduationCap, Images } from 'lucide-react';
import { ReportsApi } from '@/services/reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { toast } from '@/stores/toast.store';
import { cn } from '@/lib/cn';

type Roster = 'students' | 'staff';

const ROSTERS: { key: Roster; label: string; description: string; icon: typeof Users }[] = [
  { key: 'students', label: 'Students', description: 'Full student roster — admission, class/section, guardian details.', icon: GraduationCap },
  { key: 'staff', label: 'Staff', description: 'Full staff roster — employee ID, designation, department, contact.', icon: Users },
];

/** downloadExport()/downloadZip() use `responseType: 'blob'`, so an error
 * JSON body (e.g. NO_CURRENT_ACADEMIC_YEAR) arrives as a Blob, not parsed
 * JSON — `e.response.data.error.message` is undefined without this, so the
 * UI would show axios's generic "Request failed with status code 404"
 * instead of the real, actionable message. */
async function errMsg(e: unknown): Promise<string> {
  const anyE = e as any;
  const data = anyE?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message ?? 'Something went wrong';
    } catch {
      return 'Something went wrong';
    }
  }
  return data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Data Export — bulk roster export (CSV/Excel) + photo ZIP download (matches the real legacy "Download Images" buttons; see reports.service.ts's studentImagesZip()/staffImagesZip() doc comments for the "only already-uploaded photos" caveat). No ID-card generation this round (see docs/DEVIATIONS.md). */
export function DataExportPage() {
  const [roster, setRoster] = useState<Roster>('students');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [imagesBusy, setImagesBusy] = useState<'students' | 'teachers' | null>(null);

  const run = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await (roster === 'students' ? ReportsApi.exportStudents(format) : ReportsApi.exportStaff(format));
    } catch (e) {
      toast.error(await errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const runImages = async (type: 'students' | 'teachers') => {
    if (imagesBusy) return;
    setImagesBusy(type);
    try {
      await (type === 'students' ? ReportsApi.exportStudentImages() : ReportsApi.exportTeacherImages());
    } catch (e) {
      toast.error(await errMsg(e));
    } finally {
      setImagesBusy(null);
    }
  };

  return (
    <>
      <PageHeader title="Data Export" description="Download a full roster as CSV or Excel." />

      <div className="grid max-w-xl grid-cols-2 gap-3">
        {ROSTERS.map(({ key, label, description, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setRoster(key)}
            className={cn(
              'rounded-lg border p-4 text-left transition-colors',
              roster === key ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-slate-200 bg-white hover:bg-slate-50',
            )}
          >
            <Icon className={cn('mb-2 h-5 w-5', roster === key ? 'text-brand-600' : 'text-slate-400')} />
            <div className="text-sm font-semibold text-slate-900">{label}</div>
            <div className="mt-1 text-xs text-slate-500">{description}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => run('csv')}
          disabled={!!busy}
        >
          {busy === 'csv' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4 text-slate-600" />}
          Download CSV
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => run('xlsx')}
          disabled={!!busy}
        >
          {busy === 'xlsx' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}
          Download Excel
        </button>
      </div>

      <div className="mt-8 max-w-xl border-t border-slate-200 pt-6">
        <h2 className="text-sm font-semibold text-slate-900">Photos</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Download a ZIP of uploaded photos, one file per person named by admission number / employee ID. Only
          people with a photo already uploaded through this app are included.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => runImages('students')}
            disabled={!!imagesBusy}
          >
            {imagesBusy === 'students' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Images className="mr-1.5 h-4 w-4 text-slate-600" />}
            Download Student Photos
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => runImages('teachers')}
            disabled={!!imagesBusy}
          >
            {imagesBusy === 'teachers' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Images className="mr-1.5 h-4 w-4 text-slate-600" />}
            Download Teacher Photos
          </button>
        </div>
      </div>
    </>
  );
}
